import { FieldValues, useForm } from 'react-hook-form';
import { Template } from './Template';
import Image from 'next/image';
import {
	PaymentTabs,
	PaymentDropdown,
	ModalHeader,
} from '@/components/dashboard';
import { PaypalForm, VenmoForm } from '@/components/dashboard';
import { useEffect, useState } from 'react';
import {
	actionRequiredService,
	GET_PAYPAL_ACCOUNT,
	GET_VENMO_ACCOUNT,
	paymentService,
	userService,
} from '@/api';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { useQuery } from '@tanstack/react-query';
import { Button, LoaderScreen, Typography } from '@/components/common';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';
import { HttpResponse, PaymentType, UserI } from '@/interfaces';
import useModal from '@/hooks/useModal';
import AppRoutes from '@/const/routes';

interface PaymentDetailsProps {
	userInformation?: UserI;
	refetch: () => void;
}

export type OptionType = {
	label: string;
	name: PaymentType;
	icon: JSX.Element;
	isAdded?: boolean;
	component: React.FC<any>;
};

export const PaymentDetails: React.FC<PaymentDetailsProps> = ({
	refetch,
	userInformation,
}) => {
	const router = useRouter();
	const { actionRequiredId, caseId, fromCaseAction } = router.query;

	const { toastSuccess, toastError } = useNotification();
	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});
	const [selectedIndex, setSelectedIndex] = useState<number>(-1); // undefined
	const [paymentSelected, setPaymentPreference] = useState<
		PaymentType | null | undefined
	>();
	const [paymentToDelete, setPaymentToDelete] = useState<PaymentType>();
	const [disabledButtons, setDisabledButtons] = useState<boolean>(true);
	const { Modal, show, hide } = useModal();

	const {
		data: paypal,
		isLoading,
		refetch: refetchPaypal,
	} = useQuery({
		queryKey: [GET_PAYPAL_ACCOUNT],
		queryFn: () => paymentService.getPaypal(),
	});

	const { data: venmo, refetch: refetchVenmo } = useQuery({
		queryKey: [GET_VENMO_ACCOUNT],
		queryFn: () => paymentService.getVenmo(),
	});

	const {
		watch,
		reset,
		register,
		setValue,
		handleSubmit,
		formState: { errors },
		clearErrors,
	} = useForm({ mode: 'onBlur' });

	useEffect(() => {
		if (paypal?.data) {
			setValue('paypalEmail', paypal.data.paypalAccount);
		}
	}, [paypal]);

	useEffect(() => {
		if (venmo?.data) {
			setValue('accountName', venmo?.data.name);
			setValue('accountAlias', venmo?.data.alias);
			setValue('phoneNumber', venmo?.data.phoneNumber);
		}
	}, [venmo]);

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (userInformation) {
			timeout = setTimeout(() => {
				setPaymentPreference(userInformation.payment_preference);
				setValue('payment', userInformation.payment_preference);
			}, 500);
		}

		() => clearTimeout(timeout);
	}, [userInformation]);

	const options: OptionType[] = [
		// {
		// 	label: 'Personal Checking Account',
		// 	name: 'personal-account',
		// 	icon: <Icon src={Icons.emptyWallet} className="!w-5 !h-5" />,
		// 	component: PersonalAccountForm,
		// },
		{
			label: 'Paypal',
			name: 'paypal' as PaymentType,
			icon: (
				<Image
					src={'/img/dashboard/paypal.png'}
					alt="paypal icon"
					width={18}
					height={18}
				/>
			),
			isAdded: !!paypal?.data.paypalAccount,
			component: PaypalForm,
		},
		{
			label: 'Venmo',
			name: 'venmo' as PaymentType,
			icon: (
				<Image
					src={'/img/dashboard/venmo.png'}
					alt="venmo ico"
					width={20}
					height={20}
				/>
			),
			isAdded: !!venmo?.data.alias,
			component: VenmoForm,
		},
	];

	const handleSave = async (data: FieldValues): Promise<void> => {
		try {
			if (selectedIndex === 0) {
				await updatePaypal(data);
				if (!venmo?.data.name) {
					reset();
				}
				refetchPaypal();
				toastSuccess('Your payment details have been saved successfully.');
			}
			if (selectedIndex === 1) {
				await handleVenmoAccount(data);
				if (!paypal?.data.paypalAccount) {
					reset();
				}
				refetchVenmo();
				toastSuccess('Your payment details have been saved successfully.');
			}
			await new Promise((resolve) => setTimeout(resolve, 1000));
			await handlePreference(); // confirm payment preference
			refetch();
			approvalActionRequired(); // approval action if it comes from a required action
		} catch (error: any) {
			if (
				!navigator.onLine ||
				error.message?.includes('net::ERR_INTERNET_DISCONNECTED')
			) {
				toastError(
					formatErrorMessage('An error has occurred. Please try again later.')
				);
				return;
			}
			if (selectedIndex === 0) {
				toastError('Paypal email account is required.');
				return;
			}
			toastError(formatErrorMessage(error.message));
		} finally {
			clearErrors();
		}
	};

	const handlePreference = async (): Promise<void> => {
		try {
			let paymentPreference = '';
			if (paymentSelected) {
				paymentPreference = paymentSelected;
			} else {
				if (selectedIndex === 0) {
					paymentPreference = 'paypal';
				} else {
					paymentPreference = 'venmo';
				}
			}
			await handleSetPaymentPreferences(paymentPreference as PaymentType);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const approvalActionRequired = async (): Promise<void> => {
		if (actionRequiredId) {
			const id = Number(actionRequiredId);
			await actionRequiredService.putApprovalAction(id, true);
			if (caseId) router.push(`${AppRoutes.CLIENTAPP_MY_CASES}/${caseId}`);
		}
	};

	const updatePaypal = async (
		data: FieldValues
	): Promise<HttpResponse<{ message: string }>> => {
		if (paypal?.data.paypalAccount && !data.paypalEmail) {
			return await paymentService.deletePaypal();
		}

		const parsedEmail = data.paypalEmail.toLowerCase().trim();
		return await paymentService.updatePaypal(parsedEmail);
	};

	const handleVenmoAccount = async (
		data: FieldValues
	): Promise<HttpResponse<{ message: string }>> => {
		const payload = {
			email: data.accountName.toLowerCase().trim(),
			alias: data.accountAlias,
			phoneNumber: data.phoneNumber,
		};
		const hasValues = Object.values(payload).some((item) => !!item);

		if (venmo?.data.name && hasValues) {
			return await paymentService.updateVenmo(payload);
		}

		if (venmo?.data.name && !hasValues) {
			return await paymentService.deleteVenmo();
		}

		return await paymentService.createVenmo(payload);
	};

	const handleCancel = (): void => {
		if (selectedIndex === 0) {
			setValue('paypalEmail', paypal?.data.paypalAccount);
		}

		if (selectedIndex === 1) {
			setValue('accountName', venmo?.data.name);
			setValue('accountAlias', venmo?.data.alias);
			setValue('phoneNumber', venmo?.data.phoneNumber);
		}
		clearErrors();
		setSelectedIndex(-1);
	};

	const handleDeleteAccount = async (): Promise<void> => {
		try {
			if (paymentToDelete === 'paypal') {
				await paymentService.deletePaypal();
			}
			if (paymentToDelete === 'venmo') {
				await paymentService.deleteVenmo();
			}
			refetch();
			refetchPaypal();
			refetchVenmo();
			toastSuccess('Payment account successfully deleted.');
			hide();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			clearErrors();
			setSelectedIndex(-1);
		}
	};

	const handleSetPaymentPreferences = async (paymentType: PaymentType) => {
		if (!paymentType || paymentType === userInformation?.payment_preference)
			return;
		try {
			setPaymentPreference(paymentType);
			toastSuccess(
				fromCaseAction === 'true'
					? 'Selected payment preference.'
					: 'Payment preference has been successfully selected.'
			);
			await userService.updatePaymentPreference(paymentType);
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		}
	};

	return (
		<>
			{isDesktopOrLaptop && (
				<Template
					title="Payment Preferences"
					className="hidden lg:block"
					onClickSave={handleSubmit(handleSave)}
					onClickCancel={handleCancel}
					saveDisabled={disabledButtons}
					cancelDisabled={disabledButtons}
				>
					{isLoading && <LoaderScreen className="mt-10" />}
					{!isLoading && paymentSelected !== undefined && (
						<PaymentTabs
							watch={watch}
							register={register}
							errors={errors}
							setValue={setValue}
							options={options}
							selectedIndex={selectedIndex}
							setSelectedIndex={setSelectedIndex}
							paymentSelected={paymentSelected}
							setPaymentSelected={handleSetPaymentPreferences}
							setDisabledButtons={setDisabledButtons}
							setPaymentToDelete={setPaymentToDelete}
							showDeleteModal={show}
						/>
					)}
				</Template>
			)}
			{!isDesktopOrLaptop && paymentSelected !== undefined && (
				<PaymentDropdown
					watch={watch}
					register={register}
					errors={errors}
					setValue={setValue}
					options={options}
					className="block lg:hidden"
					onClickSave={handleSubmit(handleSave)}
					onClickCancel={handleCancel}
					paymentSelected={paymentSelected}
					setPaymentSelected={handleSetPaymentPreferences}
					setPaymentToDelete={setPaymentToDelete}
					showDeleteModal={show}
					setSelectedIndex={setSelectedIndex}
					selectedIndex={selectedIndex}
				/>
			)}
			<Modal
				withXButton
				title={<ModalHeader title="Delete Payment Method?" />}
				classNameCardContainer="w-full md:w-[532px]"
			>
				<div className="flex flex-col items-center gap-8">
					<div>
						<Typography type="regular-16" className="text-center">
							Once this payment method is deleted, all data
						</Typography>
						<Typography type="regular-16" className="text-center">
							associated with it will be permanently removed. Please
						</Typography>
						<Typography type="regular-16" className="text-center">
							confirm to proceed.
						</Typography>
					</div>
					<div className="flex items-center gap-2">
						<Button decoration="secondary" size="medium" onClick={hide}>
							Cancel
						</Button>
						<Button
							decoration="danger-outline"
							size="medium"
							onClick={handleDeleteAccount}
						>
							Delete
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};
